/**
 * `flow` template — a two-column product-flow ribbon (UKDC → destination), drawn as plain JSX
 * rather than reusing the field's imperative `drawFlow` / `drawSankeyBig`
 * (`sku-velocity/field/lenses.ts`): those build DETACHED SVG via direct DOM writes, so they
 * can't carry React hover handlers, and `drawSankeyBig`'s fixed 880×420 four-column layout
 * scales down badly into a measured box — tiny text, plus two decorative "plant"/"in-market"
 * columns with no data behind them, eating most of the width. This renders the SAME two-column
 * ribbon at BOTH sizes, at the box's own measured pixel size (see `useChartSize`), so text
 * stays legible and "expanded" only ever means more room, never smaller type.
 *
 * Reuses the pure aggregation (`chanDests`) and the two DOM-agnostic geometry helpers the
 * field's own engines already share (`ribbon`, `labelStack`) — only the "write it to the DOM"
 * half is new, and it's now React elements instead of `appendChild`.
 */

import { useMemo, useState } from "react";

import { chanDests, type Dest, labelStack } from "@/features/sku-velocity/field/lenses";
import { ribbon } from "@/features/sku-velocity/field/util";
import type { FlowView } from "@/lib/api";
import type { Sku } from "@/lib/data";
import { evalCohort } from "@/lib/specs/evaluate";

import { ChartTooltip } from "./chart-tooltip";
import { HIGHLIGHT_PRIMARY, INK } from "./colors";
import { useChartSize } from "./use-chart-size";
import type { ViewChrome, ViewSize } from "./view-shell";

interface FlowChartProps {
  spec: FlowView;
  size: ViewSize;
  skus: readonly Sku[];
  chrome?: ViewChrome;
}

/** One destination's laid-out geometry — both the site (baseline) band and, when a selection
 *  is set, the slice of it the selection accounts for. */
interface FlowNode {
  dest: Dest;
  srcY0: number;
  srcY1: number;
  dstY0: number;
  dstY1: number;
  selSrcY0: number;
  selSrcY1: number;
  selDstY0: number;
  selDstY1: number;
  labelY: number;
}

export function FlowChart({ spec, size, skus, chrome = "full" }: FlowChartProps) {
  const box = useChartSize();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // `mode` is optional in the generated type (Pydantic default), so pin the same "channel"
  // default the backend applies before it reaches `chanDests`.
  const mode = spec.mode ?? "channel";
  const sel = useMemo(
    () => (spec.cohort ? evalCohort(spec.cohort, skus) : null),
    [spec.cohort, skus],
  );
  const dests = useMemo(() => chanDests({ view: skus, sel, chanMode: mode }), [skus, sel, mode]);
  const hasSel = sel !== null;

  const w = box.width;
  const h = box.height;
  const isCard = size === "card";
  const fontSize = isCard ? 10 : 11;

  const tot = dests.reduce((a, d) => a + d.siteV, 0) || 1;
  const order = [...dests].sort((a, b) => b.siteV - a.siteV);

  // Node/ribbon layout — the same shares `drawFlow` computes, kept as plain values (rather than
  // DOM writes) so each destination can carry `onMouseEnter`/`onMouseLeave`.
  const nw = Math.max(11, w * 0.045);
  const x1 = w * 0.03;
  const x2 = w * 0.5;
  const top = h * 0.12;
  const usable = Math.max(1, h * 0.76);
  const gap = Math.max(3, h * 0.03);
  const avail = Math.max(1, usable - gap * Math.max(0, order.length - 1));

  const nodes: FlowNode[] = [];
  if (w > 0 && h > 0) {
    let srcY = top;
    let dstY = top;
    const labelPitch = fontSize * 2.3;
    const stack = labelStack(labelPitch);
    for (const dest of order) {
      const srcH = (dest.siteV / tot) * usable;
      const dstH = Math.max(2, (dest.siteV / tot) * avail);
      const selSrcH = (dest.selV / tot) * usable;
      const selDstH = (dest.selV / tot) * avail;
      nodes.push({
        dest,
        srcY0: srcY,
        srcY1: srcY + srcH,
        dstY0: dstY,
        dstY1: dstY + dstH,
        selSrcY0: srcY + srcH - selSrcH,
        selSrcY1: srcY + srcH,
        selDstY0: dstY + dstH - selDstH,
        selDstY1: dstY + dstH,
        labelY: stack(dstY + dstH / 2 + fontSize * 0.35),
      });
      srcY += srcH;
      dstY += dstH + gap;
    }
  }

  // Longest label that fits right of the destination column at this box's own width —
  // truncated with an ellipsis rather than clipped at (or overflowing) the right edge.
  const labelX = x2 + nw + 6;
  const maxLabelChars = Math.max(3, Math.floor((w - labelX - 6) / (fontSize * 0.62)));
  function truncate(label: string): string {
    return label.length > maxLabelChars
      ? `${label.slice(0, Math.max(1, maxLabelChars - 1))}…`
      : label;
  }

  const hovered = hoverIdx !== null ? nodes[hoverIdx] : undefined;
  const tipX = hovered ? ((x1 + x2 + nw) / 2 / w) * 100 : 0;
  const tipY = hovered ? ((hovered.dstY0 + hovered.dstY1) / 2 / h) * 100 : 0;

  const siteUnits = dests.reduce((a, d) => a + d.siteV, 0);
  const selUnits = hasSel ? dests.reduce((a, d) => a + d.selV, 0) : 0;

  return (
    <div className="relative flex h-full min-h-[90px] min-w-0 flex-col gap-1">
      <div ref={box.ref} className="relative min-h-0 min-w-0 flex-1">
        {w > 0 && h > 0 ? (
          <svg width={w} height={h}>
            <rect x={x1} y={top} width={nw} height={usable} rx={2} fill={INK} />
            <text x={x1} y={top - 5} fontSize={fontSize} fill="#81766F">
              UKDC
            </text>
            {nodes.map((n, i) => {
              const pct = Math.round((n.dest.siteV / tot) * 100);
              const hasSelBand = hasSel && n.selDstY1 - n.selDstY0 > 0.3;
              return (
                <g key={n.dest.label}>
                  <path
                    d={ribbon(x1 + nw, n.srcY0, n.srcY1, x2, n.dstY0, n.dstY1)}
                    fill={n.dest.color}
                    fillOpacity={hasSel ? 0.16 : 0.42}
                    className="cursor-default"
                    onMouseEnter={() => setHoverIdx(i)}
                    onMouseLeave={() => setHoverIdx((cur) => (cur === i ? null : cur))}
                  />
                  {hasSelBand ? (
                    <path
                      d={ribbon(x1 + nw, n.selSrcY0, n.selSrcY1, x2, n.selDstY0, n.selDstY1)}
                      fill={HIGHLIGHT_PRIMARY}
                      fillOpacity={0.85}
                      className="cursor-default"
                      onMouseEnter={() => setHoverIdx(i)}
                      onMouseLeave={() => setHoverIdx((cur) => (cur === i ? null : cur))}
                    />
                  ) : null}
                  <rect
                    x={x2}
                    y={n.dstY0}
                    width={nw}
                    height={n.dstY1 - n.dstY0}
                    rx={2}
                    fill={n.dest.color}
                    fillOpacity={hasSel ? 0.3 : 0.85}
                    tabIndex={0}
                    role="img"
                    aria-label={`${n.dest.label}: ${pct}% of flow`}
                    className="cursor-default outline-none"
                    onMouseEnter={() => setHoverIdx(i)}
                    onMouseLeave={() => setHoverIdx((cur) => (cur === i ? null : cur))}
                    onFocus={() => setHoverIdx(i)}
                    onBlur={() => setHoverIdx((cur) => (cur === i ? null : cur))}
                  />
                  {hasSelBand ? (
                    <rect
                      x={x2}
                      y={n.selDstY0}
                      width={nw}
                      height={n.selDstY1 - n.selDstY0}
                      rx={2}
                      fill={HIGHLIGHT_PRIMARY}
                    />
                  ) : null}
                  <text x={labelX} y={n.labelY} fontSize={fontSize} fill="#564C47">
                    {truncate(n.dest.label)} {pct}%
                  </text>
                </g>
              );
            })}
          </svg>
        ) : null}
      </div>
      {hovered ? (
        <ChartTooltip x={`${tipX}%`} y={`${tipY}%`}>
          <div className="font-semibold">{hovered.dest.label}</div>
          <div>
            {Math.round((hovered.dest.siteV / tot) * 100)}% of flow ·{" "}
            {Math.round(hovered.dest.siteV).toLocaleString()} boxes/mo
          </div>
          {hasSel ? (
            <div>
              {hovered.dest.selV > 0
                ? `selection: ${Math.round(
                    (hovered.dest.selV / (hovered.dest.siteV || 1)) * 100,
                  )}% of this destination`
                : "selection: none here"}
            </div>
          ) : null}
        </ChartTooltip>
      ) : null}
      {chrome === "bare" ? null : (
        <p className="text-muted-foreground shrink-0 text-[11px]">
          {hasSel
            ? selUnits > 0
              ? `${Math.round(selUnits).toLocaleString()} boxes/mo in the selection — ${(
                  (selUnits / (siteUnits || 1)) *
                  100
                ).toFixed(0)}% of flow`
              : "None — no shipments in the window"
            : `${Math.round(siteUnits).toLocaleString()} boxes/mo, whole site`}
        </p>
      )}
    </div>
  );
}
